/**
 * A measuring sitting: one class, everyone in it, six figures each (D-440).
 *
 * <b>Six, not eleven.</b> The five garment sizes belong to the parent and the
 * import — `asked`: *"t shirts are mostly configured by parents, these are more
 * concreate measurements done by staff"* (D-441). They travel on a different
 * route so a save from here cannot touch them (D-442).
 */

/** The six a tape answers. Field order follows `measurementFields()`'s `body` group. */
export type BodyMeasurements = {
  bust: string | null
  waist: string | null
  hip: string | null
  girth: string | null
  height: string | null
  inseam: string | null
}

export type MeasuringRow = {
  dancerId: string
  name: string
  /**
   * What she already holds.
   *
   * <b>Never rendered as blank because `measuredOn` is null.</b> 45 of the
   * biggest class's 64 carry imported figures and no date, so "not measured
   * yet" and "has nothing" are different states — and treating them as one
   * deletes a real waist and hip on the first girth anybody saves (D-442).
   */
  measurements: BodyMeasurements
  /** Null means nobody has put a tape to her, whatever figures she holds. */
  measuredOn: string | null
  /** Answered per dancer by the server; a substitute may not (D-286). */
  mayRecord: boolean
}

export type MeasuringClass = {
  classId: string
  name: string
  dancers: MeasuringRow[]
}

export const BODY_FIELDS: { key: keyof BodyMeasurements; label: string }[] = [
  { key: 'bust', label: 'Bust' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'girth', label: 'Girth' },
  { key: 'height', label: 'Height' },
  { key: 'inseam', label: 'Inseam' },
]

export async function fetchMeasuringClass(classId: string): Promise<MeasuringClass> {
  const res = await fetch(`/api/admin/dancers/measuring/${classId}`)
  if (!res.ok) throw new Error(`Could not load the class (${res.status})`)
  return res.json()
}

export async function saveBodyMeasurements(
  dancerId: string,
  body: BodyMeasurements,
): Promise<void> {
  const res = await fetch(`/api/admin/dancers/${dancerId}/body-measurements`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) throw new Error(`Could not save (${res.status})`)
}

/**
 * Whether a row has been measured by the studio, as opposed to holding figures.
 *
 * <b>The date is the only signal, deliberately.</b> Figures alone cannot say —
 * an import brings them across with no date because it cannot know when they
 * were taken (D-198), and a parent editing a T-shirt size must never read as
 * the studio having measured her child (D-238).
 */
export const isMeasured = (row: MeasuringRow): boolean => row.measuredOn !== null

/**
 * Rows whose name contains what she typed, case-insensitively.
 *
 * <b>Names, because the queue is not the roster.</b> `asked`: whoever is
 * standing in front of her is next, not the next one alphabetically — so
 * reaching a named dancer has to be typing, not scrolling 64 rows.
 *
 * <b>Anything with a name, since D-745</b>: the banked-classes list asks the
 * same question of a different row, and a second four-line copy of *does this
 * name contain what she typed* is exactly the drift A-17 calls a defect. It
 * keeps this home because this is where it was written and moving it would
 * churn every caller for nothing.
 */
export function matching<T extends { name: string }>(rows: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...rows]
  return rows.filter((r) => r.name.toLowerCase().includes(needle))
}

/** How many still have nobody's tape against them. */
export const outstanding = (rows: MeasuringRow[]): number =>
  rows.filter((r) => !isMeasured(r)).length

/**
 * The studio's today as a local-midnight <c>Date</c>, for comparing against a
 * stored date.
 *
 * <b>Both sides have to be parsed the same way, and mine were not</b> (D-455).
 * `measurementAge` reads a stored `yyyy-mm-dd` as *local* midnight; the first
 * version of this screen handed it `new Date(\`${today()}T00:00:00Z\`)`, which
 * is *UTC* midnight — seven in the evening the day before, in Central. So every
 * row read one day ahead of itself and said <i>"Dated in the future"</i> the
 * moment it was saved. Reported by the owner within minutes of the deploy.
 *
 * The simulated date (D-386) still has to come from `today()`, or a testing
 * round under a moved clock reads every row as future-dated — which is the
 * failure this was trying to avoid in the first place.
 */
export function studioToday(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Where a dancer stands, for reading a roster of 64 at a glance. */
export type Freshness = 'never' | 'recent' | 'stale'

/**
 * <b>Measured, and how recently</b> (D-455).
 *
 * `asked`: <i>"should it just be updated to Completed or something and maybe
 * shade that as green so its quick/easy to see that they are done?"</i> — the
 * colour is the answer to that, because at 64 rows the eye wants a state, not a
 * sentence.
 *
 * <b>Three states rather than done/not-done</b>, because "completed" stops
 * being true without anything changing. A nine-year-old's girth moves in a
 * season, and next September every row would read <i>Completed</i> while
 * carrying year-old figures — the exact reading `MeasuredOn` was added to
 * prevent. So a recent measurement is green and finished; an old one says so
 * and stays visible as work.
 *
 * Thirty days because a measuring pass is a few sittings across a couple of
 * weeks, so it covers one and never two seasons.
 */
export function freshness(row: MeasuringRow, today: Date): Freshness {
  if (!row.measuredOn) return 'never'

  const [y, m, d] = row.measuredOn.split('-').map(Number)
  const days = Math.floor((today.getTime() - new Date(y, m - 1, d).getTime()) / 86_400_000)

  return days >= 0 && days < 30 ? 'recent' : 'stale'
}
